import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Select,
    MenuItem,
    Button,
    Box,
    InputLabel,
    FormControl,
    useTheme,
    CircularProgress
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import VersionInfo from '../../components/version/VersionInfo';

interface EnvironmentOption {
    name: string;
    url: string;
    product?: string;
}

interface EnvironmentConfig {
    environments: EnvironmentOption[];
    forcedEnvironment?: string;
    defaultEnvironment?: EnvironmentOption;
    hideSelector?: boolean;
}

const EnvironmentSelector = ({ onEnvironmentSelected }) => {
    const theme = useTheme();
    const [selectedEnv, setSelectedEnv] = useState('');
    const [environments, setEnvironments] = useState<EnvironmentOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [defaultEnvMode, setDefaultEnvMode] = useState(false);
    const [defaultEnv, setDefaultEnv] = useState<EnvironmentOption | null>(null);

    // Helper function to get the effective URL (use base href if no URL provided)
    const getEffectiveUrl = (url: string): string => {
        if (!url || url.trim() === '') {
            // When no URL is provided, use the origin (root of the server)
            // The client code will add the API path prefix (e.g., /admin/)
            // So we just need the server root here
            return window.location.origin;
        }
        return url;
    };

    // Fetch environment configuration on mount
    useEffect(() => {
        const fetchEnvironmentConfig = async () => {
            try {
                // Call the admin API to get environment configuration
                const response = await fetch('/admin/environments');
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch environment config: ${response.statusText}`);
                }
                
                // Check if response is actually JSON
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    throw new Error(`Invalid response type: expected JSON but got ${contentType || 'unknown'}`);
                }
                
                let config: EnvironmentConfig;
                try {
                    config = await response.json();
                } catch (jsonError) {
                    throw new Error(`Failed to parse JSON response: ${jsonError.message}`);
                }
                
                // Validate the config structure
                if (!config || !Array.isArray(config.environments)) {
                    throw new Error('Invalid environment config structure: missing or invalid environments array');
                }
                
                // Store environments in state
                setEnvironments(config.environments);

                // Default environment mode short-circuits the selector UI
                if (config.defaultEnvironment) {
                    const env = config.defaultEnvironment;
                    const effectiveUrl = getEffectiveUrl(env.url);
                    setDefaultEnvMode(true);

                    sessionStorage.setItem('selectedEnv', env.name);
                    sessionStorage.setItem('selectedEnvUrl', effectiveUrl);
                    sessionStorage.setItem('selectedEnvProduct', env.product || 'neo');
                    sessionStorage.setItem('defaultEnvironment', 'true');

                    setDefaultEnv({
                        name: env.name,
                        url: effectiveUrl,
                        product: env.product || 'neo'
                    });
                    
                    setLoading(false);
                    return;
                }
                
                // If there's a forced environment, auto-select it
                if (config.forcedEnvironment) {
                    const forcedEnv = config.environments.find(
                        env => env.name === config.forcedEnvironment
                    );
                    
                    if (forcedEnv) {
                        // Use base href if URL is not provided
                        const effectiveUrl = getEffectiveUrl(forcedEnv.url);
                        
                        // Store in sessionStorage
                        sessionStorage.setItem('selectedEnv', forcedEnv.name);
                        sessionStorage.setItem('selectedEnvUrl', effectiveUrl);
                        if (forcedEnv.product) {
                            sessionStorage.setItem('selectedEnvProduct', forcedEnv.product);
                        }
                        
                        // Dispatch event
                        const customEvent = new CustomEvent('environmentSelected', {
                            detail: { 
                                name: forcedEnv.name, 
                                url: effectiveUrl, 
                                product: forcedEnv.product 
                            }
                        });
                        window.dispatchEvent(customEvent);
                        
                        // Call the callback to proceed
                        if (forcedEnv.product) {
                            onEnvironmentSelected({ 
                                name: forcedEnv.name, 
                                url: effectiveUrl, 
                                product: forcedEnv.product 
                            });
                        } else {
                            onEnvironmentSelected({ 
                                name: forcedEnv.name, 
                                url: effectiveUrl 
                            });
                        }
                    }
                }
                
                setLoading(false);
            } catch (err) {
                console.error('Error fetching environment config:', err);
                setError(err.message || 'Unknown error occurred');
                
                // Fallback to dev environments on error
                const devEnvironments: EnvironmentOption[] = [
                    { name: 'MM-Admin', url: 'http://localhost:5010' },
                    { name: 'Winmark Corp', url: 'http://localhost:5002' },
                    { name: `Plato's Closet`, url: 'http://localhost:5400' },
                    { name: 'Restaurant', url: 'http://localhost:5001' },
                    { name: 'Retail', url: 'http://localhost:5002' },
                    { name: 'Neo', url: 'http://localhost:5001', product: 'neo' }
                ];
                setEnvironments(devEnvironments);
                setLoading(false);
            }
        };

        fetchEnvironmentConfig();
    }, [onEnvironmentSelected]);

    const handleSelectChange = (event) => {
        const selectedName = event.target.value;
        const env = environments.find((e) => e.name === selectedName);
        
        if (env) {
            // Use base href if URL is not provided
            const effectiveUrl = getEffectiveUrl(env.url);
            
            console.log('Environment selected:', { name: env.name, url: effectiveUrl, product: env.product });
            setSelectedEnv(env.name);
            
            // Store both name and URL in sessionStorage
            sessionStorage.setItem('selectedEnv', env.name);
            sessionStorage.setItem('selectedEnvUrl', effectiveUrl);
            if(env.product) {
                sessionStorage.setItem('selectedEnvProduct', env.product);
            }
            
            // You can also dispatch a custom event if you need other parts of the app to listen
            const customEvent = new CustomEvent('environmentSelected', {
                detail: { name: env.name, url: effectiveUrl, product: env.product }
            });
            window.dispatchEvent(customEvent);
        }
    };

    const handleContinue = () => {
        const env = environments.find((e) => e.name === selectedEnv);
        if (env) {
            // Use base href if URL is not provided
            const effectiveUrl = getEffectiveUrl(env.url);
            
            // only pass env.product if it is defined   
            if(env.product) {
                onEnvironmentSelected({ name: env.name, url: effectiveUrl, product: env.product });
            } else {
                onEnvironmentSelected({ name: env.name, url: effectiveUrl });
            }
        }
    };

    // Show loading spinner while fetching config
    const handleDefaultEnvContinue = () => {
        if (!defaultEnv) return;

        const detail = { 
            name: defaultEnv.name, 
            url: defaultEnv.url, 
            product: defaultEnv.product 
        };

        const customEvent = new CustomEvent('environmentSelected', { detail });
        window.dispatchEvent(customEvent);

        onEnvironmentSelected(detail);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: theme.palette.background.default,
                    position: 'relative',
                }}
            >
                <CircularProgress size={60} />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 24,
                        right: 24,
                    }}
                >
                    <VersionInfo />
                </Box>
            </Box>
        );
    }

    if (defaultEnvMode && defaultEnv) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    position: 'relative',
                    backgroundColor: theme.palette.background.default,
                    backgroundImage: 'url("./icons/env-selection-background.gif")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <Box
                    component="img"
                    src="./icons/mad-mobile-logo.png"
                    alt="Mad Mobile logo watermark"
                    sx={{
                        position: 'fixed',
                        right: 24,
                        bottom: 24,
                        width: 320,
                        opacity: 0.75,
                        pointerEvents: 'none',
                        userSelect: 'none',
                        filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                        zIndex: 1
                    }}
                />
                <Box
                    sx={{
                        position: 'fixed',
                        top: 24,
                        right: 24,
                        zIndex: 2
                    }}
                >
                    <VersionInfo />
                </Box>
                <Card
                    elevation={12}
                    sx={{
                        minWidth: 480,
                        maxWidth: 560,
                        padding: 5,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(15px)',
                        borderRadius: 4,
                        color: 'white',
                        textAlign: 'center'
                    }}
                >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Neo Portal
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Click below to connect with your credentials.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<LoginIcon />}
                            onClick={handleDefaultEnvContinue}
                            sx={{
                                mt: 2,
                                py: 1.5,
                                fontSize: '1.05rem'
                            }}
                        >
                            Sign in to continue
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                position: 'relative',
                backgroundColor: theme.palette.background.default,
                backgroundImage: 'url("./icons/env-selection-background.gif")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <Box
                component="img"
                src="./icons/mad-mobile-logo.png"
                alt="Mad Mobile logo watermark"
                sx={{
                    position: 'fixed',
                    right: 24,
                    bottom: 24,
                    width: 320,
                    opacity: 0.75,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                    zIndex: 1
                }}
            />
            <Box
                sx={{
                    position: 'fixed',
                    top: 24,
                    right: 24,
                    zIndex: 2
                }}
            >
                <VersionInfo />
            </Box>
            <Card
                elevation={12}
                sx={{
                    minWidth: 500,
                    maxWidth: 600,
                    padding: 6,
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: 'blur(15px)',
                    WebkitBackdropFilter: 'blur(15px)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                    transform: 'scale(1.1)',
                }}
            >
                <CardContent>
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom
                        sx={{ 
                            fontWeight: 600, 
                            textAlign: 'center',
                            mb: 3,
                            color: 'rgba(255, 255, 255, 0.98)'
                        }}
                    >
                        Select Environment
                    </Typography>

                    <FormControl fullWidth sx={{ marginTop: 2 }}>
                        <InputLabel
                            id="environment-select-label"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                '&.Mui-focused': { color: 'rgba(255, 255, 255, 0.95)' }
                            }}
                        >
                            Environment
                        </InputLabel>
                        <Select
                            labelId="environment-select-label"
                            value={selectedEnv}
                            onChange={handleSelectChange}
                            label="Environment"
                            sx={{
                                fontSize: '1.1rem',
                                color: 'rgba(255, 255, 255, 0.95)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.35)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)'
                                },
                                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                        backdropFilter: 'blur(8px)',
                                        WebkitBackdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
                                    }
                                },
                                MenuListProps: {
                                    sx: { py: 0.5 }
                                }
                            }}
                        >
                            {environments.map((env) => (
                                <MenuItem
                                    key={env.name}
                                    value={env.name}
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.95)',
                                        fontWeight: 700,
                                        fontSize: '1.2rem',
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.12)'
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.18)'
                                        }
                                    }}
                                >
                                    {env.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        endIcon={<LoginIcon />}
                        onClick={handleContinue}
                        disabled={!selectedEnv}
                        sx={{ marginTop: 3 }}
                    >
                        Continue
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
};

export default EnvironmentSelector;
